import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Checkbox,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import {
    NEOFEED_USAGE_MODE_LABELS,
    NEOFEED_USAGE_MODE_MICROCOPY,
    getAllowedUsageModes,
    formatNeoFeedScore,
} from './neoFeedContextUtils';

const PRIMARY = '#f25d27';

/**
 * Cards e trechos de sugestão NeoFeed usados pelo `NeoFeedSuggestionsPanel`,
 * exibido na coluna lateral das telas de escrita/reescrita de matéria.
 *
 * @typedef {import('../../../types/articles').NeoFeedContextSuggestion} NeoFeedContextSuggestion
 * @typedef {import('../../../types/articles').NeoFeedContextSelectionEntry} NeoFeedContextSelectionEntry
 */

export const formatNeoFeedSuggestionDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
    });
};

/** Uma fonte já selecionada — resumo compacto com opção de remover. */
export const NeoFeedSelectedSourceChip = ({ entry, disabled, simplified = false, onRemove }) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.4,
        borderRadius: '8px',
        backgroundColor: `${PRIMARY}0c`,
        border: `1px solid ${PRIMARY}30`,
        maxWidth: '100%',
    }}>
        <Typography sx={{
            fontSize: '0.72rem',
            color: PRIMARY,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
        }}>
            {entry.suggestion?.title || `Notícia #${entry.suggestion?.external_article_id}`}
        </Typography>
        {!simplified && (
            <Chip
                label={NEOFEED_USAGE_MODE_LABELS[entry.usage_mode] ?? entry.usage_mode}
                size="small"
                sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, backgroundColor: PRIMARY, color: '#fff', flexShrink: 0 }}
            />
        )}
        <Tooltip title="Remover fonte">
            <span>
                <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={onRemove}
                    sx={{ p: 0.2, color: PRIMARY, flexShrink: 0 }}
                >
                    <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </span>
        </Tooltip>
    </Box>
);

/** Trecho (chunk) selecionável de uma sugestão. */
const NeoFeedChunkRow = ({ chunk, checked, disabled, onToggle }) => (
    <Box
        onClick={disabled ? undefined : onToggle}
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
            p: '4px 6px',
            borderRadius: '6px',
            cursor: disabled ? 'default' : 'pointer',
            backgroundColor: checked ? '#fff' : 'transparent',
            border: `1px solid ${checked ? '#e0e0e0' : 'transparent'}`,
        }}
    >
        <Checkbox
            checked={checked}
            onChange={disabled ? undefined : onToggle}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
            size="small"
            sx={{ p: 0.2, mt: '-2px', color: '#ccc', '&.Mui-checked': { color: PRIMARY } }}
        />
        <Typography sx={{
            fontSize: '0.72rem',
            color: '#666',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
        }}>
            {chunk.text}
        </Typography>
    </Box>
);

/**
 * Card de uma sugestão retornada pelo acervo NeoFeed: metadados, motivo,
 * link externo e, quando selecionada, modo de uso + trechos selecionáveis.
 *
 * No modo `simplified` (usado no painel de página "Sugestões NeoFeed"), o
 * card mostra apenas título, autor, data de publicação e o botão de acessar
 * a matéria na íntegra — sem tags/score/motivo e sem as opções de modo de
 * uso ou de trechos ao selecionar (o modo de uso é sempre "factual" e todos
 * os trechos disponíveis são usados automaticamente).
 *
 * @param {{
 *   suggestion: NeoFeedContextSuggestion;
 *   entry?: NeoFeedContextSelectionEntry;
 *   disabled?: boolean;
 *   simplified?: boolean;
 *   onToggleSelected: (suggestion: NeoFeedContextSuggestion) => void;
 *   onToggleChunk: (externalArticleId: string, chunkId: string) => void;
 *   onChangeUsageMode: (externalArticleId: string, mode: string) => void;
 * }} props
 */
export const NeoFeedSuggestionCard = ({ suggestion, entry, disabled, simplified = false, onToggleSelected, onToggleChunk, onChangeUsageMode }) => {
    const isSelected = Boolean(entry);
    const allowedModes = getAllowedUsageModes(suggestion);
    const dateLabel = formatNeoFeedSuggestionDate(suggestion.published_at);
    const scoreLabel = formatNeoFeedScore(suggestion.score);
    const hasChunks = Array.isArray(suggestion.chunks) && suggestion.chunks.length > 0;

    return (
        <Box sx={{
            border: `1px solid ${isSelected ? PRIMARY : '#eee'}`,
            backgroundColor: isSelected ? `${PRIMARY}06` : '#fff',
            borderRadius: '10px',
            p: '10px 12px',
            transition: 'all 0.12s',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelected(suggestion)}
                    disabled={disabled}
                    size="small"
                    sx={{ p: 0.3, mt: '-4px', color: '#ccc', '&.Mui-checked': { color: PRIMARY } }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#333', lineHeight: 1.35 }}>
                        {suggestion.title}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.7, mt: 0.4 }}>
                        {suggestion.author && (
                            <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>{suggestion.author}</Typography>
                        )}
                        {dateLabel && (
                            <Typography sx={{ fontSize: '0.7rem', color: '#aaa' }}>{dateLabel}</Typography>
                        )}
                        {!simplified && (suggestion.tags || []).slice(0, 2).map((tag) => (
                            <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.6rem', borderColor: '#ddd', color: '#777' }}
                            />
                        ))}
                        {!simplified && scoreLabel && (
                            <Typography sx={{ fontSize: '0.68rem', color: '#aaa' }}>{scoreLabel} relevância</Typography>
                        )}
                    </Box>
                    {!simplified && suggestion.reason && (
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', mt: 0.4, fontStyle: 'italic' }}>
                            {suggestion.reason}
                        </Typography>
                    )}
                </Box>
                <Tooltip title={suggestion.url ? 'Abrir notícia original' : 'Link não disponível'}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={!suggestion.url}
                            onClick={() => window.open(suggestion.url, '_blank', 'noopener,noreferrer')}
                            sx={{ color: '#999', mt: '-4px', '&:hover': { color: PRIMARY, backgroundColor: `${PRIMARY}10` } }}
                        >
                            <OpenInNewIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {isSelected && !simplified && (
                <Box sx={{ mt: 1, pl: '30px', display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    <FormControl size="small" fullWidth>
                        <Select
                            value={entry.usage_mode}
                            disabled={disabled}
                            onChange={(e) => onChangeUsageMode(suggestion.external_article_id, e.target.value)}
                            sx={{ fontSize: '0.78rem', '& .MuiSelect-select': { py: 0.6 } }}
                        >
                            {allowedModes.map((mode) => (
                                <MenuItem key={mode} value={mode} sx={{ fontSize: '0.8rem' }}>
                                    {NEOFEED_USAGE_MODE_LABELS[mode] ?? mode}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '0.68rem', color: '#999', lineHeight: 1.4 }}>
                        {NEOFEED_USAGE_MODE_MICROCOPY[entry.usage_mode]}
                    </Typography>

                    {hasChunks ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#999' }}>
                                Trechos relevantes
                            </Typography>
                            {suggestion.chunks.map((chunk) => (
                                <NeoFeedChunkRow
                                    key={chunk.chunk_id}
                                    chunk={chunk}
                                    disabled={disabled}
                                    checked={entry.selected_chunk_ids.includes(chunk.chunk_id)}
                                    onToggle={() => onToggleChunk(suggestion.external_article_id, chunk.chunk_id)}
                                />
                            ))}
                            {entry.selected_chunk_ids.length === 0 && (
                                <Typography sx={{ fontSize: '0.68rem', color: '#c77', mt: 0.3 }}>
                                    Sem trechos selecionados; o backend poderá escolher trechos automaticamente.
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '0.68rem', color: '#aaa' }}>
                            Sem trechos sugeridos; o backend usará os primeiros trechos da notícia.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};
